import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import {
    obtenerConfigEnvio,
    guardarConfigEnvio,
    listarCorreosNotificacion,
    guardarCorreoNotificacion,
    eliminarCorreoNotificacion
} from '../../services/Custodia/CustodiaService';

const TIPOS = [
    { label: 'Para', value: 'PARA' },
    { label: 'Copia', value: 'COPIA' }
];

export const CustodiaEnvioConfig = ({ visible, onHide }) => {
    const toast = useRef(null);
    const [config, setConfig] = useState({ coberturaMinimaPct: 80, coberturaForzarMinPct: 50, bloqueMinutos: 10, validarEstatusMinutos: 30 });
    const [correos, setCorreos] = useState([]);
    const [nuevoCorreo, setNuevoCorreo] = useState({ correo: '', tipo: 'PARA' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible) {
            cargar();
        }
    }, [visible]);

    const cargar = async () => {
        try {
            setLoading(true);
            const [cfg, lst] = await Promise.all([obtenerConfigEnvio(), listarCorreosNotificacion()]);
            if (cfg?.data) setConfig(cfg.data);
            setCorreos(lst?.data || []);
        } catch (e) {
            console.error('Error cargando configuración de envío:', e);
        } finally {
            setLoading(false);
        }
    };

    const onGuardarConfig = async () => {
        try {
            await guardarConfigEnvio(config);
            toast.current?.show({ severity: 'success', summary: 'Parámetros guardados', life: 2500 });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'No se pudo guardar', life: 3000 });
        }
    };

    const onAgregarCorreo = async () => {
        if (!nuevoCorreo.correo || !nuevoCorreo.correo.includes('@')) {
            toast.current?.show({ severity: 'warn', summary: 'Correo inválido', life: 2500 });
            return;
        }
        try {
            await guardarCorreoNotificacion({ correo: nuevoCorreo.correo.trim(), tipo: nuevoCorreo.tipo, activo: true });
            setNuevoCorreo({ correo: '', tipo: 'PARA' });
            const lst = await listarCorreosNotificacion();
            setCorreos(lst?.data || []);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'No se pudo agregar', life: 3000 });
        }
    };

    const onEliminarCorreo = async (row) => {
        try {
            await eliminarCorreoNotificacion(row.id);
            setCorreos((prev) => prev.filter((c) => c.id !== row.id));
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'No se pudo eliminar', life: 3000 });
        }
    };

    const accionesCorreo = (row) => (
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" tooltip="Quitar" onClick={() => onEliminarCorreo(row)} />
    );

    return (
        <Dialog header="Configuración — Aviso de custodias retenidas" visible={visible} style={{ width: '720px' }} modal onHide={onHide}>
            <Toast ref={toast} />

            <h6>Parámetros de validación</h6>
            <div className="formgrid grid">
                <div className="field col-12 md:col-3">
                    <label>Cobertura mínima (%)</label>
                    <InputNumber value={config.coberturaMinimaPct} onValueChange={(e) => setConfig({ ...config, coberturaMinimaPct: e.value })} min={0} max={100} />
                </div>
                <div className="field col-12 md:col-3">
                    <label>Mínimo para forzar (%)</label>
                    <InputNumber value={config.coberturaForzarMinPct} onValueChange={(e) => setConfig({ ...config, coberturaForzarMinPct: e.value })} min={0} max={100} />
                </div>
                <div className="field col-12 md:col-3">
                    <label>Tamaño de bloque (min)</label>
                    <InputNumber value={config.bloqueMinutos} onValueChange={(e) => setConfig({ ...config, bloqueMinutos: e.value })} min={1} max={120} />
                </div>
                <div className="field col-12 md:col-3">
                    <label>Validar estatus (min)</label>
                    <InputNumber value={config.validarEstatusMinutos} onValueChange={(e) => setConfig({ ...config, validarEstatusMinutos: e.value })} min={1} max={1440} />
                </div>
            </div>
            <p className="text-sm text-color-secondary mt-0 mb-3">
                <b>Cobertura mínima</b>: bajo este % la custodia se retiene. <b>Mínimo para forzar</b>: si la cobertura queda entre este piso y el mínimo, al enviar se pide confirmación; por debajo del piso no se puede enviar.
            </p>
            <div className="flex justify-content-end mb-4">
                <Button label="Guardar parámetros" icon="pi pi-save" className="p-button-sm" onClick={onGuardarConfig} />
            </div>

            <h6>Correos del aviso interno</h6>
            <p className="text-sm text-color-secondary mt-0">Cuentas a las que se avisa cuando una custodia queda retenida. "Para" = destinatario, "Copia" = CC.</p>
            <div className="formgrid grid align-items-end">
                <div className="field col-12 md:col-7">
                    <label>Correo</label>
                    <InputText value={nuevoCorreo.correo} onChange={(e) => setNuevoCorreo({ ...nuevoCorreo, correo: e.target.value })} placeholder="cuenta@dominio.com" />
                </div>
                <div className="field col-8 md:col-3">
                    <label>Tipo</label>
                    <Dropdown value={nuevoCorreo.tipo} options={TIPOS} onChange={(e) => setNuevoCorreo({ ...nuevoCorreo, tipo: e.value })} />
                </div>
                <div className="field col-4 md:col-2">
                    <Button label="Agregar" icon="pi pi-plus" className="p-button-sm" onClick={onAgregarCorreo} />
                </div>
            </div>

            <DataTable value={correos} loading={loading} responsiveLayout="scroll" emptyMessage="Sin correos configurados">
                <Column field="correo" header="Correo" />
                <Column field="tipo" header="Tipo" style={{ width: '8rem' }} />
                <Column body={accionesCorreo} style={{ width: '6rem' }} />
            </DataTable>
        </Dialog>
    );
};
